import React, { useState, useEffect } from 'react';
import { Table, Spinner, Alert, Card, Badge, Button } from 'react-bootstrap';
import Navbar from './Navbar';
import API_CONFIG, { buildApiUrl } from '../config/api';

const PaymentHistory = () => {
    const [groups, setGroups] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [monthColumns, setMonthColumns] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            const decoded = JSON.parse(atob(token.split(".")[1]));
            setGroups(decoded["cognito:groups"] || []);
        }
    }, []);

    useEffect(() => {
        if (groups.includes("mtkd-admin") || groups.includes("mtkd-finance")) {
            generateAvailableYears();
            initializeDefaultMonths();
        }
    }, [groups]);

    useEffect(() => {
        if (selectedYear && selectedMonths.length > 0) {
            generateMonthColumns();
            fetchPaymentHistory();
        }
    }, [groups, selectedYear, selectedMonths, selectedAgeGroup]);

    const generateAvailableYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        // Generate years from 2020 to current year + 1
        for (let year = 2020; year <= currentYear + 1; year++) {
            years.push(year);
        }
        setAvailableYears(years);
    };

    const initializeDefaultMonths = () => {
        const currentYear = new Date().getFullYear();
        const lastYear = currentYear - 1;
        const defaultMonths = [];
        
        // Add December of last year
        defaultMonths.push({ year: lastYear, month: 12, label: `Dec ${lastYear}` });
        
        // Add all months for current year
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        monthNames.forEach((monthName, index) => {
            defaultMonths.push({ 
                year: currentYear, 
                month: index + 1, 
                label: `${monthName} ${currentYear}` 
            });
        });
        
        setSelectedMonths(defaultMonths);
    };

    const generateMonthColumns = () => {
        const months = selectedMonths.map(monthData => monthData.label);
        setMonthColumns(months);
    };

    const handleYearChange = (year) => {
        setSelectedYear(parseInt(year));
        // Reset months when year changes
        const newMonths = [];
        const previousYear = parseInt(year) - 1;
        
        // Add December of previous year
        newMonths.push({ 
            year: previousYear, 
            month: 12, 
            label: `Dec ${previousYear}` 
        });
        
        // Add all months for selected year
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        monthNames.forEach((monthName, index) => {
            newMonths.push({ 
                year: parseInt(year), 
                month: index + 1, 
                label: `${monthName} ${year}` 
            });
        });
        
        setSelectedMonths(newMonths);
    };

    const handleMonthToggle = (monthData) => {
        setSelectedMonths(prev => {
            const isSelected = prev.some(m => m.year === monthData.year && m.month === monthData.month);
            if (isSelected) {
                // Don't allow deselecting if it's the only month selected
                if (prev.length <= 1) return prev;
                return prev.filter(m => !(m.year === monthData.year && m.month === monthData.month));
            } else {
                return [...prev, monthData];
            }
        });
    };

    const getAvailableMonths = () => {
        const months = [];
        const previousYear = selectedYear - 1;
        
        // Add December of previous year
        months.push({ 
            year: previousYear, 
            month: 12, 
            label: `Dec ${previousYear}`,
            key: `${previousYear}-12`
        });
        
        // Add all months for selected year
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        monthNames.forEach((monthName, index) => {
            months.push({ 
                year: selectedYear, 
                month: index + 1, 
                label: `${monthName} ${selectedYear}`,
                key: `${selectedYear}-${(index + 1).toString().padStart(2, '0')}`
            });
        });
        
        return months;
    };

    const fetchPaymentHistory = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            
            // Prepare query parameters
            const params = new URLSearchParams();
            selectedMonths.forEach(monthData => {
                params.append('months', `${monthData.year}-${monthData.month.toString().padStart(2, '0')}`);
            });
            
            // Add age_group if selected
            if (selectedAgeGroup) {
                params.append('age_group', selectedAgeGroup);
            }
            
            const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENT_HISTORY)}?${params.toString()}`;
            console.log('Fetching payment history from:', url);
            console.log('Selected months:', selectedMonths);
            console.log('Selected age group:', selectedAgeGroup);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const data = await response.json();
            console.log('Payment history data received:', data);
            setPaymentHistory(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching payment history:', err);
            setError(`Failed to load payment history: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return amount ? `$${parseFloat(amount).toFixed(2)}` : '-';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const getPaymentStatusBadge = (status) => {
        const badgeProps = {
            'Paid': { bg: 'success', text: 'Paid' },
            'Partial': { bg: 'warning', text: 'Partial' },
            'Unpaid': { bg: 'danger', text: 'Unpaid' }
        };
        
        const props = badgeProps[status] || { bg: 'secondary', text: 'Unknown' };
        return <Badge bg={props.bg}>{props.text}</Badge>;
    };

    const renderMonthlyPayment = (monthlyData) => {
        if (!monthlyData || monthlyData.length === 0) {
            return <span className="text-muted">-</span>;
        }

        return monthlyData.map((payment, index) => (
            <div key={index} className="mb-1">
                <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">{payment.debt_type_desc}</small>
                    {getPaymentStatusBadge(payment.status)}
                </div>
                <div className="small">
                    <div>Owed: {formatCurrency(payment.debt_amount)}</div>
                    {payment.debt_discount > 0 && (
                        <div>Discount: {formatCurrency(payment.debt_discount)}</div>
                    )}
                    {payment.debt_paid_amount > 0 && (
                        <div>Paid: {formatCurrency(payment.debt_paid_amount)}</div>
                    )}
                    {payment.debt_paid_date && (
                        <div className="text-muted">Date: {formatDate(payment.debt_paid_date)}</div>
                    )}
                </div>
                {index < monthlyData.length - 1 && <hr className="my-1" />}
            </div>
        ));
    };

    const getCurrentYearRange = () => {
        const currentYear = new Date().getFullYear();
        const lastYear = currentYear - 1;
        return `${lastYear}-12-01 to ${currentYear}-12-31`;
    };

    if (!groups.includes("mtkd-admin") && !groups.includes("mtkd-finance")) {
        return (
            <div>
                <Navbar groups={groups} />
                <div className="container mt-4">
                    <Alert variant="danger">
                        <Alert.Heading>Access Denied</Alert.Heading>
                        <p>You don't have permission to view payment history.</p>
                    </Alert>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div>
                <Navbar groups={groups} />
                <div className="container mt-4">
                    <Card>
                        <Card.Body className="text-center">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="mt-2">Loading payment history...</p>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Navbar groups={groups} />
                <div className="container mt-4">
                    <Alert variant="danger">
                        <Alert.Heading>Error Loading Payment History</Alert.Heading>
                        <p>{error}</p>
                        <Button variant="outline-danger" onClick={fetchPaymentHistory}>
                            Try Again
                        </Button>
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Navbar groups={groups} />
            <div className="container mt-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>Payment History</h2>
                    <Button variant="outline-primary" onClick={fetchPaymentHistory}>
                        Refresh Data
                    </Button>
                </div>

                {/* Filter Controls */}
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">Filters</h5>
                    </Card.Header>
                    <Card.Body>
                        <div className="row mb-3">
                            <div className="col-md-3">
                                <label className="form-label">Year:</label>
                                <select 
                                    className="form-select" 
                                    value={selectedYear} 
                                    onChange={(e) => handleYearChange(e.target.value)}
                                >
                                    {availableYears.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Filter by Age Group:</label>
                                <select 
                                    className="form-select" 
                                    value={selectedAgeGroup || ''} 
                                    onChange={(e) => setSelectedAgeGroup(e.target.value || null)}
                                >
                                    <option value="">All Students</option>
                                    <option value="toddlers">Toddlers (&lt; 6 years)</option>
                                    <option value="juniors">Juniors (7 to 12 years)</option>
                                    <option value="teens">Teens (13 to 17 years)</option>
                                    <option value="adults">Adults (18+ years)</option>
                                </select>
                                <small className="text-muted">
                                    Filter students by age group
                                </small>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Months:</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {getAvailableMonths().map((monthData, index) => {
                                        const isSelected = selectedMonths.some(m => 
                                            m.year === monthData.year && m.month === monthData.month
                                        );
                                        return (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`btn btn-sm ${isSelected ? 'btn-success' : 'btn-outline-secondary'}`}
                                                onClick={() => handleMonthToggle(monthData)}
                                            >
                                                {monthData.label} {isSelected ? '✓' : '○'}
                                            </button>
                                        );
                                    })}
                                </div>
                                <small className="text-muted">
                                    Click months to toggle selection (✓ = selected, ○ = not selected). At least one month must be selected.
                                </small>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>
                        <h5 className="mb-0">Payment History Report</h5>
                        <small className="text-muted">
                            Showing payment history for: {getCurrentYearRange()}
                        </small>
                    </Card.Header>
                    <Card.Body>
                        {paymentHistory.length === 0 ? (
                            <Alert variant="info">
                                <Alert.Heading>No Payment History Found</Alert.Heading>
                                <p>No payment records found for the current year and December of last year.</p>
                            </Alert>
                        ) : (
                            <div className="payment-history-table-container">
                                <style jsx>{`
                                    .payment-history-table-container {
                                        position: relative;
                                        overflow: auto;
                                        max-height: 70vh;
                                    }
                                    
                                    .payment-history-table {
                                        width: 100%;
                                        border-collapse: separate;
                                        border-spacing: 0;
                                    }
                                    
                                    /* First header row - all cells sticky at top 0 */
                                    .payment-history-table thead tr:nth-child(1) th {
                                        position: sticky;
                                        top: 0;
                                        z-index: 30;
                                    }
                                    
                                    /* Second header row - sticky below first row (estimated 48px height) */
                                    .payment-history-table thead tr:nth-child(2) th {
                                        position: sticky;
                                        top: 48px;
                                        z-index: 30;
                                    }
                                    
                                    /* Frozen column - Student Name (for body cells) */
                                    .frozen-col-1 {
                                        position: sticky;
                                        left: 0;
                                        background: white;
                                        z-index: 10;
                                        box-shadow: 2px 0 5px rgba(0,0,0,0.1);
                                    }
                                    
                                    /* Student Name header - frozen both vertically and horizontally with highest z-index */
                                    .frozen-header-1 {
                                        position: sticky !important;
                                        left: 0 !important;
                                        top: 0 !important;
                                        background: #212529 !important;
                                        z-index: 60 !important;
                                        box-shadow: 2px 0 5px rgba(0,0,0,0.1);
                                    }
                                    
                                    /* Tbody cells should stay below headers */
                                    .payment-history-table tbody .frozen-col-1 {
                                        z-index: 5;
                                    }
                                    
                                    /* Remove border spacing to prevent gaps */
                                    .payment-history-table thead tr {
                                        margin: 0;
                                        padding: 0;
                                    }
                                    
                                    .payment-history-table thead th {
                                        margin: 0;
                                        padding: 0.75rem 0.5rem;
                                    }
                                    
                                    // .frozen-header-2 {
                                    //     position: sticky;
                                    //     left: 200px;
                                    //     top: 0;
                                    //     background: #212529 !important;
                                    //     z-index: 40;
                                    //     box-shadow: 2px 0 5px rgba(0,0,0,0.1);
                                    // }
                                    
                                    // .frozen-header-3 {
                                    //     position: sticky;
                                    //     left: 300px;
                                    //     top: 0;
                                    //     background: #212529 !important;
                                    //     z-index: 40;
                                    //     box-shadow: 2px 0 5px rgba(0,0,0,0.1);
                                    // }
                                    
                                    // .frozen-header-4 {
                                    //     position: sticky;
                                    //     left: 420px;
                                    //     top: 0;
                                    //     background: #212529 !important;
                                    //     z-index: 40;
                                    //     box-shadow: 2px 0 5px rgba(0,0,0,0.1);
                                    // }
                                    
                                    .month-column {
                                        min-width: 120px;
                                        max-width: 150px;
                                    }
                                    
                                    .student-name-col {
                                        width: 200px;
                                        min-width: 200px;
                                    }
                                    
                                    .rank-col {
                                        width: 100px;
                                        min-width: 100px;
                                    }
                                    
                                    .phone-col {
                                        width: 120px;
                                        min-width: 120px;
                                    }
                                    
                                    .tuition-due-col {
                                        width: 120px;
                                        min-width: 120px;
                                    }
                                    
                                    .sub-column {
                                        width: 80px;
                                        min-width: 80px;
                                        font-size: 0.8rem;
                                    }
                                `}</style>
                                <Table striped bordered hover size="sm" className="payment-history-table">
                                    <thead className="table-dark">
                                        <tr>
                                            <th rowSpan="2" className="frozen-header-1 frozen-col-1 student-name-col align-middle">Student Name</th>
                                            <th rowSpan="2" className="rank-col align-middle">Rank</th>
                                            <th rowSpan="2" className="phone-col align-middle">Telephone</th>
                                            <th rowSpan="2" className="tuition-due-col align-middle">Tuition Due Day</th>
                                            {selectedMonths.map((monthData, index) => (
                                                <th key={index} colSpan="3" className="text-center month-column">
                                                    {monthData.label}
                                                </th>
                                            ))}
                                        </tr>
                                        <tr>
                                            {selectedMonths.map((monthData, index) => (
                                                <React.Fragment key={index}>
                                                    <th className="text-center sub-column">Amount</th>
                                                    <th className="text-center sub-column">Discount</th>
                                                    <th className="text-center sub-column">Paid</th>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentHistory.map((student) => (
                                            <tr key={student.student_id}>
                                                <td className="frozen-col-1 student-name-col">
                                                    <strong>{student.student_name}</strong>
                                                </td>
                                                <td className="rank-col">
                                                    {student.current_rank || 'N/A'}
                                                </td>
                                                <td className="phone-col">
                                                    {student.phone || 'N/A'}
                                                </td>
                                                <td className="tuition-due-col">
                                                    {student.tuition_due_day || 'N/A'}
                                                </td>
                                                {selectedMonths.map((monthData, index) => {
                                                    const monthKey = `${monthData.year}-${monthData.month.toString().padStart(2, '0')}`;
                                                    const monthlyData = student.monthly_payments[monthKey];
                                                    
                                                    // Debug logging
                                                    if (student.student_name === 'John Doe' && index === 0) {
                                                        console.log('Debug payment data:', {
                                                            student: student.student_name,
                                                            monthKey,
                                                            monthlyData,
                                                            allPayments: student.monthly_payments
                                                        });
                                                    }
                                                    
                                                    // Get the first payment for this month (or create empty structure)
                                                    const payment = monthlyData && monthlyData.length > 0 ? monthlyData[0] : null;
                                                    
                                                    return (
                                                        <React.Fragment key={index}>
                                                            <td className="text-center sub-column">
                                                                {payment ? formatCurrency(payment.debt_amount) : '-'}
                                                            </td>
                                                            <td className="text-center sub-column">
                                                                {payment ? formatCurrency(payment.debt_discount) : '-'}
                                                            </td>
                                                            <td className="text-center sub-column">
                                                                {payment ? (
                                                                    <div>
                                                                        <div>{formatCurrency(payment.debt_paid_amount)}</div>
                                                                        {payment.debt_paid_date && (
                                                                            <small className="text-muted">{formatDate(payment.debt_paid_date)}</small>
                                                                        )}
                                                                    </div>
                                                                ) : '-'}
                                                            </td>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {paymentHistory.length > 0 && (
                    <Card className="mt-4">
                        <Card.Header>
                            <h5 className="mb-0">Summary</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="text-center">
                                        <h4 className="text-primary">
                                            {paymentHistory.length}
                                        </h4>
                                        <small className="text-muted">Total Students</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="text-center">
                                        <h4 className="text-success">
                                            {formatCurrency(
                                                paymentHistory.reduce((total, student) => {
                                                    return total + Object.values(student.monthly_payments).reduce((monthTotal, monthlyData) => {
                                                        if (monthlyData && monthlyData.length > 0) {
                                                            return monthTotal + monthlyData.reduce((paymentTotal, payment) => {
                                                                return paymentTotal + (payment.debt_paid_amount || 0);
                                                            }, 0);
                                                        }
                                                        return monthTotal;
                                                    }, 0);
                                                }, 0)
                                            )}
                                        </h4>
                                        <small className="text-muted">Total Paid</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="text-center">
                                        <h4 className="text-warning">
                                            {formatCurrency(
                                                paymentHistory.reduce((total, student) => {
                                                    return total + Object.values(student.monthly_payments).reduce((monthTotal, monthlyData) => {
                                                        if (monthlyData && monthlyData.length > 0) {
                                                            return monthTotal + monthlyData.reduce((paymentTotal, payment) => {
                                                                const totalOwed = (payment.debt_amount || 0) - (payment.debt_discount || 0);
                                                                const totalPaid = payment.debt_paid_amount || 0;
                                                                return paymentTotal + Math.max(0, totalOwed - totalPaid);
                                                            }, 0);
                                                        }
                                                        return monthTotal;
                                                    }, 0);
                                                }, 0)
                                            )}
                                        </h4>
                                        <small className="text-muted">Outstanding</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="text-center">
                                        <h4 className="text-info">
                                            {paymentHistory.filter(student => {
                                                return Object.values(student.monthly_payments).every(monthlyData => {
                                                    if (!monthlyData || monthlyData.length === 0) return true;
                                                    return monthlyData.every(payment => {
                                                        const totalPaid = payment.debt_paid_amount || 0;
                                                        const totalOwed = (payment.debt_amount || 0) - (payment.debt_discount || 0);
                                                        return totalPaid >= totalOwed;
                                                    });
                                                });
                                            }).length}
                                        </h4>
                                        <small className="text-muted">Fully Paid</small>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default PaymentHistory;
